import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarView = ({ 
  events, 
  selectedDate, 
  viewMode, 
  onDateSelect, 
  onViewModeChange, 
  onEventSelect,
  onCreateEvent 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarTheme, setCalendarTheme] = useState('default');
  const [showCustomization, setShowCustomization] = useState(false);
  
  // Color themes for calendar customization
  const themes = [
    { 
      id: 'default', 
      name: 'Default', 
      primary: '#0ea5e9', 
      secondary: '#e0f2fe',
      accent: '#0369a1'
    },
    { 
      id: 'purple', 
      name: 'Purple', 
      primary: '#9333ea', 
      secondary: '#f3e8ff',
      accent: '#7c3aed'
    },
    { 
      id: 'green', 
      name: 'Green', 
      primary: '#059669', 
      secondary: '#d1fae5',
      accent: '#047857'
    },
    { 
      id: 'orange', 
      name: 'Orange', 
      primary: '#ea580c', 
      secondary: '#fed7aa',
      accent: '#c2410c'
    }
  ];

  // Load saved theme preference
  useEffect(() => {
    const savedTheme = localStorage?.getItem('calendar_theme');
    if (savedTheme) {
      setCalendarTheme(savedTheme);
    }
  }, []);

  // Save theme preference
  const handleThemeChange = (themeId) => {
    setCalendarTheme(themeId);
    localStorage?.setItem('calendar_theme', themeId);
  };

  // Generate calendar days for month view
  const generateCalendarDays = () => {
    const year = currentMonth?.getFullYear();
    const month = currentMonth?.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate?.setDate(startDate?.getDate() - firstDay?.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days?.push(new Date(current));
      current?.setDate(current?.getDate() + 1);
    }
    
    return days;
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events?.filter(event => 
      format(new Date(event?.start_time), 'yyyy-MM-dd') === dateStr
    ) || [];
  };

  // Get event type icon
  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'call': return 'Phone';
      case 'meeting': return 'Users';
      case 'follow_up': return 'ArrowRight';
      case 'reminder': return 'Bell';
      case 'deadline': return 'Clock';
      case 'demo': return 'Monitor';
      case 'consultation': return 'MessageSquare';
      default: return 'Calendar';
    }
  };

  // Get selected theme
  const selectedTheme = themes?.find(theme => theme?.id === calendarTheme) || themes?.[0];

  const renderMonthView = () => {
    const days = generateCalendarDays();
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <div className="bg-card border border-border rounded-lg">
        {/* Month Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-foreground">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(prev => new Date(prev?.getFullYear(), prev?.getMonth() - 1, 1))}
                iconName="ChevronLeft"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date())}
                className="text-xs"
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(prev => new Date(prev?.getFullYear(), prev?.getMonth() + 1, 1))}
                iconName="ChevronRight"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCustomization(!showCustomization)}
              iconName="Palette"
              className="text-muted-foreground"
            >
              Customize
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onCreateEvent}
              iconName="Plus"
            >
              Add Event
            </Button>
          </div>
        </div>

        {/* Customization Panel */}
        {showCustomization && (
          <div className="p-4 border-b border-border bg-muted/50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">Calendar Theme</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCustomization(false)}
                iconName="X"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {themes?.map(theme => (
                <button
                  key={theme?.id}
                  onClick={() => handleThemeChange(theme?.id)}
                  className={`
                    p-3 rounded-lg border transition-all
                    ${calendarTheme === theme?.id 
                      ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: theme?.primary }}
                    />
                    <span className="text-sm font-medium">{theme?.name}</span>
                  </div>
                  <div className="flex space-x-1">
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: theme?.primary }}
                    />
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: theme?.secondary }}
                    />
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: theme?.accent }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Week Headers */}
        <div className="grid grid-cols-7 gap-0 border-b border-border">
          {weekDays?.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-0">
          {days?.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            const isSelected = format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
            const isCurrentMonth = day?.getMonth() === currentMonth?.getMonth();
            const eventsWithNotes = dayEvents?.filter(e => e?.description?.trim());
            
            return (
              <div
                key={index}
                onClick={() => onDateSelect?.(day)}
                className={`
                  min-h-[120px] p-2 border-r border-b border-border cursor-pointer transition-colors
                  ${isCurrentMonth ? 'bg-background' : 'bg-muted/30'}
                  ${isSelected ? 'bg-primary/10' : ''}
                  hover:bg-muted/50
                `}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`
                    text-sm font-medium
                    ${!isCurrentMonth ? 'text-muted-foreground' : 'text-foreground'}
                    ${isToday ? 'text-primary font-bold' : ''}
                  `}>
                    {day?.getDate()}
                  </span>
                  <div className="flex items-center space-x-1">
                    {dayEvents?.length > 0 && (
                      <span 
                        className="text-xs px-1.5 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: selectedTheme?.primary }}
                      >
                        {dayEvents?.length}
                      </span>
                    )}
                    {eventsWithNotes?.length > 0 && (
                      <Icon 
                        name="StickyNote" 
                        size={10} 
                        className="text-yellow-500"
                        title={`${eventsWithNotes?.length} events with notes`}
                      />
                    )}
                  </div>
                </div>
                
                <div className="space-y-1">
                  {dayEvents?.slice(0, 3)?.map((event, eventIndex) => (
                    <div
                      key={event?.id || eventIndex}
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEventSelect?.(event);
                      }}
                      className="text-xs p-1 rounded truncate cursor-pointer transition-colors hover:opacity-80"
                      style={{ 
                        backgroundColor: selectedTheme?.secondary,
                        color: selectedTheme?.accent
                      }}
                      title={`${event?.title}${event?.description ? '\nNote: ' + event?.description : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1 flex-1 min-w-0">
                          <Icon 
                            name={getEventTypeIcon(event?.event_type)} 
                            size={8}
                          />
                          <span className="truncate">{event?.title}</span>
                        </div>
                        {event?.description?.trim() && (
                          <Icon name="StickyNote" size={8} className="text-yellow-600 flex-shrink-0" />
                        )}
                      </div>
                      {event?.description && (
                        <div className="text-xs opacity-75 truncate mt-0.5">
                          {event?.description}
                        </div>
                      )}
                    </div>
                  ))}
                  {dayEvents?.length > 3 && (
                    <div 
                      className="text-xs p-1 rounded text-center cursor-pointer"
                      style={{ 
                        backgroundColor: selectedTheme?.primary,
                        color: 'white'
                      }}
                      onClick={(e) => {
                        e?.stopPropagation();
                        onDateSelect?.(day);
                        onViewModeChange?.('day');
                      }}
                    >
                      +{dayEvents?.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="text-center text-muted-foreground">
          <Icon name="Calendar" size={48} className="mx-auto mb-2" />
          <p>Week view coming soon</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewModeChange?.('month')}
            className="mt-2"
          >
            Switch to Month View
          </Button>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDate(selectedDate);
    
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDateSelect?.(new Date(selectedDate?.getTime() - 24 * 60 * 60 * 1000))}
              iconName="ChevronLeft"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDateSelect?.(new Date())}
              className="text-xs"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDateSelect?.(new Date(selectedDate?.getTime() + 24 * 60 * 60 * 1000))}
              iconName="ChevronRight"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={onCreateEvent}
              iconName="Plus"
            >
              Add Event
            </Button>
          </div>
        </div>
        
        <div className="p-4">
          {dayEvents?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Calendar" size={48} className="mx-auto mb-2" />
              <p>No events scheduled for this day</p>
              <Button
                variant="outline"
                size="sm"
                onClick={onCreateEvent}
                iconName="Plus"
                className="mt-3"
              >
                Add Event
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {dayEvents?.map(event => (
                <div
                  key={event?.id}
                  onClick={() => onEventSelect?.(event)}
                  className="p-4 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  style={{ borderLeftColor: selectedTheme?.primary, borderLeftWidth: '4px' }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon 
                          name={getEventTypeIcon(event?.event_type)} 
                          size={16}
                          style={{ color: selectedTheme?.primary }}
                        />
                        <h4 className="font-medium text-foreground">{event?.title}</h4>
                        <span 
                          className="text-xs px-2 py-1 rounded-full text-white capitalize"
                          style={{ backgroundColor: selectedTheme?.primary }}
                        >
                          {event?.event_type}
                        </span>
                        {event?.description?.trim() && (
                          <Icon name="StickyNote" size={14} className="text-yellow-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {format(new Date(event?.start_time), 'HH:mm')} - {format(new Date(event?.end_time), 'HH:mm')}
                      </p>
                      {event?.description && (
                        <div className="text-sm text-muted-foreground mb-2 bg-yellow-50 border border-yellow-200 rounded p-3">
                          <div className="flex items-start space-x-2">
                            <Icon name="StickyNote" size={14} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-yellow-800 mb-1">Event Notes:</p>
                              <p className="text-yellow-700">{event?.description}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {event?.location && (
                        <p className="text-sm text-muted-foreground">
                          <Icon name="MapPin" size={12} className="inline mr-1" />
                          {event?.location}
                        </p>
                      )}
                    </div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* View Mode Switcher */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {['month', 'week', 'day']?.map(mode => (
            <Button
              key={mode}
              variant={viewMode === mode ? 'default' : 'outline'}
              size="sm"
              onClick={() => onViewModeChange?.(mode)}
              className="capitalize"
            >
              {mode}
            </Button>
          ))}
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-sm text-muted-foreground">
            Theme: <span className="font-medium" style={{ color: selectedTheme?.primary }}>
              {selectedTheme?.name}
            </span>
          </div>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-primary"></div>
              <span>Events</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="StickyNote" size={10} className="text-yellow-500" />
              <span>With Notes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Views */}
      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}
    </div>
  );
};

export default CalendarView;